<template>
  <div class="history-modal">
    <HistList
      :list="normalizedList"
      :loading="computedLoading"
      :model-name="modelName"
      :model-id="modelId"
      :on-export="modelName === 'audit_trail' ? downloadAuditTrail : null"
      :on-query-change="onQueryChange"
      :server-total="serverTotal"
      :server-authors="serverAuthors"
      ref="listRef"
    />

    <div class="d-flex justify-content-center mt-3 mb-3">
      <button
        type="button"
        class="btn btn-outline-primary"
        @click="$emit('modalstack:close', false)"
      >
        {{ t('common.ok') }}
      </button>
    </div>
  </div>
</template>
<script setup>
import HistList from '@/components/history/list.vue'
import { computed, toValue, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { format } from 'date-fns'
import { storeToRefs } from 'pinia'
import { useMessageStack } from '@/stores/messagestack'
import { useUserStore } from '@/stores/user'
import config from '@/config'

defineEmits(['modalstack:close'])
const props = defineProps({
  list: {
    type: [Object, Array],
    default: () => [],
  },
  loading: { type: [Boolean, Object], default: true },
  modelName: String,
  modelId: Number,
  /** Текущий сервис в модалке Audit Trail (ref или строка): rtms | catalogs | sso | all */
  auditTrailService: {
    type: [String, Object],
    default: 'rtms',
  },
  /** Audit Trail: смена страницы/фильтров — запрос новой страницы с бэка. */
  onQueryChange: { type: Function, default: null },
})
const listRef = ref()
const exportPending = ref(false)

// AS-1477: при смене сервиса в Audit Trail сбрасываем страницу и фильтры
// списка — иначе у нового сервиса открывается страница старого (например,
// пустая 3-я в SSO, где страница всего одна)
watch(
  () => {
    const v = props.auditTrailService
    return v && typeof v === 'object' && 'value' in v ? v.value : v
  },
  () => {
    listRef.value?.resetFilters?.({ emitQuery: false })
  }
)
const { push } = useMessageStack()
const { t } = useI18n()
const { user, isAuthorized } = storeToRefs(useUserStore())

defineExpose({
  listRef,
})

function resolveService() {
  const v = props.auditTrailService
  if (v && typeof v === 'object' && 'value' in v) {
    return v.value ?? 'rtms'
  }
  return v || 'rtms'
}

async function downloadAuditTrail() {
  const params = new URLSearchParams()
  params.set('service', resolveService())
  const ext = listRef.value?.getExportParams?.()
  if (ext?.authors?.length) {
    ext.authors.forEach(a => params.append('authors', a))
  }
  if (ext?.dateFrom) {
    params.set('date_from', format(new Date(ext.dateFrom), 'yyyy-MM-dd'))
  }
  if (ext?.dateTo) {
    params.set('date_to', format(new Date(ext.dateTo), 'yyyy-MM-dd'))
  }
  if (!isAuthorized.value || !user.value?.access_token) {
    push(t('history.ui.sessionExpired'))
    return
  }

  exportPending.value = true
  try {
    const apiBase = config.VITE_API_URL || `${window.location.origin}/api/v1.0/`
    const exportUrl = new URL(`audit_trail/export?${params.toString()}`, apiBase).href
    const res = await fetch(exportUrl, {
      credentials: 'include',
      cache: 'no-cache',
      headers: {
        Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, */*',
        Authorization: `Bearer ${user.value.access_token}`,
      },
    })
    if (!res.ok) {
      push(t('history.ui.exportFailed'))
      return
    }
    const blob = await res.blob()
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = t('history.ui.reportFilename', { date: format(new Date(), 'dd.MM.yyyy') })
    a.click()
    URL.revokeObjectURL(url)
  } catch {
    push(t('history.ui.downloadError'))
  } finally {
    exportPending.value = false
  }
}

const computedLoading = computed(() =>
  typeof props.loading === 'boolean' ? props.loading : toValue(props.loading)
)
// HistList._pick() ждёт { history, related }; API истории отдаёт { total_count, data: { history, related } }
const normalizedList = computed(() => {
  const val = toValue(props.list)
  if (!val) return { history: [], related: {} }
  const data =
    val?.data && typeof val.data === 'object' && !Array.isArray(val.data) ? val.data : val
  const history = Array.isArray(data?.history) ? data.history : []
  const related =
    data?.related && typeof data.related === 'object' && !Array.isArray(data.related)
      ? data.related
      : {}
  return { history, related }
})

const serverTotal = computed(() => {
  const val = toValue(props.list)
  return typeof val?.total_count === 'number' ? val.total_count : null
})

const serverAuthors = computed(() => {
  const val = toValue(props.list)
  const authors = val?.data?.authors
  return Array.isArray(authors) ? authors : null
})
</script>
